import React, { FC, memo } from "react";
import { useTranslation } from "react-i18next";

import { Text } from "@/components/Text";
import { IAssignment } from "@/data/assignments";
import { formatFullName } from "@/data/persons";
import { Flex } from "@/eworkitComponents/components";
import { useFormats } from "@/hooks/Formats";
import { List, ListItem } from "@/views/components/List";
import { WorkOrderStatusBadge } from "@/views/components/WorkOrder";

export interface IAssignmentsListProps {
  assignments: IAssignment[];
  onRowClick?: (assignment: IAssignment) => void;
}

export const AssignmentsList: FC<IAssignmentsListProps> = memo(({ assignments, onRowClick }) => {
  const { t } = useTranslation("session");
  const { formatDate } = useFormats();

  return (
    <List innerShadows={false}>
      {assignments.map((assignment: IAssignment) => (
        <ListItem key={assignment.id} onClick={() => onRowClick?.(assignment)}>
          <Flex direction="column" innerMargin="xs">
            <Flex innerMargin="sm">
              <Flex width={6}>
                <Text color="grey2">{t("ASSIGNMENTS.LIST.TABLE_HEADERS.ID")}</Text>
              </Flex>

              <Flex width={6}>{assignment.systemId}</Flex>
            </Flex>

            <Flex innerMargin="sm">
              <Flex width={6}>
                <Text color="grey2">{t("ASSIGNMENTS.LIST.TABLE_HEADERS.REVISION")}</Text>
              </Flex>

              <Flex width={6}>{assignment.version + 1}</Flex>
            </Flex>

            <Flex innerMargin="sm">
              <Flex width={6}>
                <Text color="grey2">{t("ASSIGNMENTS.LIST.TABLE_HEADERS.JR_TITLE")}</Text>
              </Flex>

              <Flex width={6}>{assignment.jobRequestDetails.title}</Flex>
            </Flex>

            <Flex innerMargin="sm">
              <Flex width={6}>
                <Text color="grey2">{t("ASSIGNMENTS.LIST.TABLE_HEADERS.COMPANY")}</Text>
              </Flex>

              <Flex width={6}>{assignment.origin.name}</Flex>
            </Flex>

            <Flex innerMargin="sm">
              <Flex width={6}>
                <Text color="grey2">{t("ASSIGNMENTS.LIST.TABLE_HEADERS.WORKER")}</Text>
              </Flex>

              <Flex width={6}>{formatFullName(assignment.consultantProfile)}</Flex>
            </Flex>

            <Flex innerMargin="sm">
              <Flex width={6}>
                <Text color="grey2">{t("ASSIGNMENTS.LIST.TABLE_HEADERS.START_DATE")}</Text>
              </Flex>

              <Flex width={6}>{formatDate(assignment.startDate)}</Flex>
            </Flex>

            <Flex innerMargin="sm">
              <Flex width={6}>
                <Text color="grey2">{t("ASSIGNMENTS.LIST.TABLE_HEADERS.END_DATE")}</Text>
              </Flex>

              <Flex width={6}>{formatDate(assignment.actualEndDate ?? assignment.endDate)}</Flex>
            </Flex>

            <Flex innerMargin="sm">
              <Flex width={6}>
                <Text color="grey2">{t("ASSIGNMENTS.LIST.TABLE_HEADERS.STATUS")}</Text>
              </Flex>

              <Flex width={6}>
                <WorkOrderStatusBadge status={assignment.status} />
              </Flex>
            </Flex>
          </Flex>
        </ListItem>
      ))}
    </List>
  );
});
